import { Link } from 'react-router-dom';
import { Mail, MapPin, Linkedin, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: 'Revenue Cycle Optimization', href: '/services#revenue-cycle' },
    { name: 'Training & Education', href: '/services#training' },
    { name: 'DRG Validation', href: '/services#drg' },
    { name: 'Documentation Integrity', href: '/services#documentation-integrity' },
    { name: 'Denial Assistance', href: '/services#coding-denial' },
    { name: 'Physician Collaboration', href: '/services#physician-collaboration' },
  ];

  const company = [
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <footer className="bg-[#0F172A] text-white">
      {/* CTA Section */}
      <div className="border-b border-[#1e293b]">
        <div className="container-custom py-16 sm:py-20">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-2xl">
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight font-['Outfit']">
                Ready to Optimize Your Revenue?
              </h2>
              <p className="mt-4 text-lg text-gray-400">
                Let's identify your reimbursement opportunities and create a strategic roadmap for financial improvement.
              </p>
            </div>
            <Link to="/contact" data-testid="footer-cta-assessment">
              <Button className="bg-[#0D9488] hover:bg-[#0f766e] text-white rounded-md px-8 py-6 text-lg group">
                Request an Assessment
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center" data-testid="footer-logo">
              <span className="text-2xl font-bold font-['Outfit']">Symplicity</span>
              <span className="text-2xl font-light text-[#0D9488] font-['Outfit'] ml-1">Revenue</span>
            </Link>
            <p className="mt-4 text-gray-400 text-sm leading-relaxed">
              Strategic healthcare reimbursement consulting for hospitals and health systems. Maximize revenue. Reduce risk. Strengthen compliance.
            </p>
            <div className="flex items-center gap-4 mt-6">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#1e293b] flex items-center justify-center hover:bg-[#0D9488] transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Services
            </h3>
            <ul className="space-y-3">
              {services.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="footer-link text-sm"
                    data-testid={`footer-service-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {company.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="footer-link text-sm"
                    data-testid={`footer-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Contact
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-[#0D9488] flex-shrink-0 mt-0.5" />
                <a
                  href="mailto:deirdre.lewis@symplicity.org"
                  className="footer-link text-sm"
                  data-testid="footer-email"
                >
                  deirdre.lewis@symplicity.org
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#0D9488] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  Serving Healthcare Organizations Nationwide
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-[#1e293b]">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              &copy; {currentYear} Symplicity Revenue Solutions. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-sm text-gray-500 hover:text-gray-400" data-testid="footer-privacy">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-gray-500 hover:text-gray-400" data-testid="footer-terms">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
